import {
    Breakpoints,
    Text,
    TextAlignment,
    TextTag,
    TokenTextAppearance,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'
import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useDispatch, useSelector} from 'react-redux'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import containerBottomStage from '../../../assets/css-vars/containerBottomStage'
import {boxedContainer, gridUtils} from '../../../assets/css-vars/gridUtils'
import {AccordionSettings, QueryParams} from '../../../common/configuration'
import {
    setCustomerType,
    setLoading,
    setSingleCar,
} from '../../../features/general/generalSlice'
import TrackingAction from '../../../tracking-manager'
import SingleModelInterface, {
    Banners,
    Promotion,
} from '../../../types/singleModelInterface'
import {useFeatureServices} from '../../../use-feature-services'
import {
    getAccordionOpenClickData,
    getVehiclePromotionsPageloadData,
} from '../../../utils/tracking/getTrackingData'
import setTracking, {
    checkTrackingManager,
} from '../../../utils/tracking/tracking-utils/setTracking'
import {RootState} from '../../rootReducer'
import {AppDispatch, FetchData, fetchCarBySlug} from '../../thunk'
import BigBanner from '../banner/BigBanner'
import SmallBanner from '../banner/SmallBanner'
import AccordionItem from '../ui/AccordionItem'
import ShowroomBanner from '../ui/ShowroomBanner'
import ShowroomTitle from '../ui/ShowroomTitle'

import isEmpty from 'lodash.isempty'
import Brands from '../../../utils/brand/brand'
import getCustomerTypeFromUrl from '../../../utils/customerTypes/getCustomerTypeFromUrl'
import addTrailingSlash from '../../../utils/url/addTrailingSlash'
import BasicStage from '../ui/BasicStage'
import {capitalizeWords} from '../../../utils/text/dealer'
import HorizontalRFQFixedBanner from '../ui/HorizontalRFQFixedBanner'
import {openCouponLayer} from '../layers/launchers/CouponRequestLayerHandle'
import {isMobile} from '../../../shared/utilities'
import {setFormCycleID} from '../../../features/userData/userDataSlice'
import {generateRandomString} from '../../../utils/tracking/tracking-utils/generateRandomString'
import {Global} from '../../../shared/variables'
import {getShowroomOpenPromotionId} from '../../../utils/promos/getShowroomOpenPromotionId'

const SinglePromotionWrapper = styled.article`
    --single-promotion-wrapper-spacing-top: 30px;
    --single-promotion-wrapper-spacing: 0;
    ${containerBottomStage}

    padding-right: var(--single-promotion-wrapper-spacing);
    padding-left: var(--single-promotion-wrapper-spacing);
    padding-top: var(--single-promotion-wrapper-spacing-top);

    @media (min-width: ${Breakpoints.b560}px) {
        --single-promotion-wrapper-spacing-top: 0;
        --single-promotion-wrapper-spacing: 60px;
    }

    @media (min-width: ${Breakpoints.b960}px) {
        --single-promotion-wrapper-spacing: 70px;
    }

    @media (min-width: ${Breakpoints.b1280}px) {
        --single-promotion-wrapper-spacing: 0;
    }
`

const SinglePromotionContainer = styled.div`
    padding-right: ${gridUtils.gap};
    padding-left: ${gridUtils.gap};
    ${boxedContainer}

    &.Single-Promotion-is-standalone {
        padding: 0 !important;
        border-top: 1px solid ${(props) => props.theme.colors.border.divider};
    }
`

const SmallBannersWrapper = styled.div`
    display: flex;
    flex-direction: column;

    @media only screen and (min-width: ${Breakpoints.b560}px) {
        gap: 20px;
    }
`

const BannersWrapper = styled.div`
    display: grid;
    gap: 35px;
    margin-top: 150px;
    margin-bottom: 150px;

    @media only screen and (min-width: ${Breakpoints.b560}px) {
        grid-template-columns: 1fr 1.5fr;
    }

    @media only screen and (min-width: ${Breakpoints.b960}px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 60px;
    }
`

const ShowRoomBannerContainer = styled.div`
    margin-bottom: 150px;
`
const ModelNotAvailableText = styled(Text)`
    margin-bottom: 150px;
`

export interface SinglePromotionListProps {
    modelFromCms?: string | undefined
    onStatusChange?: (status: {
        hasResolvedPromotions: boolean
        hasAnyPromotions: boolean
        modelNames: string[]
    }) => void
}

// tslint:disable-next-line:cyclomatic-complexity
export function SinglePromotionList(
    props: SinglePromotionListProps
): React.JSX.Element {
    const {t} = useTranslation('app')
    // tslint:disable-next-line:no-unnecessary-type-assertion
    const dispatch = useDispatch() as AppDispatch
    let {modelName} = useParams<{modelName: string}>()

    const backendUrl = useSelector(
        (state: RootState) => state.general.backendUrl
    )
    const modelsWithActivePromotions = useSelector(
        (state: RootState) => state.general.modelsWithActivePromotions
    )
    const brandFromState = useSelector(
        (state: RootState) => state.general.brand
    )
    const apiQueryParams = useSelector(
        (state: RootState) => state.general.apiQueryParams
    )
    const previewAPIToken = useSelector(
        (state: RootState) => state.general.previewAPIToken
    )
    const dealerCode = useSelector(
        (state: RootState) => state.dealerData.dealerCode
    )
    const navigate = useHistory()
    const location = useLocation()
    const isShowroom = useSelector(
        (state: RootState) => state.general.isShowroom
    )

    if (
        typeof modelName === 'undefined' &&
        typeof props.modelFromCms !== 'undefined'
    ) {
        modelName = props.modelFromCms
    }

    const [hasResolvedPromotions, setHasResolvedPromotions] = useState(false)

    // reset previous car in session
    useEffect(() => {
        dispatch(setSingleCar({}))

        if (brandFromState === Brands.VIC && location && location.pathname) {
            const customerFromCurrentLocation = getCustomerTypeFromUrl(location)
            dispatch(setCustomerType(customerFromCurrentLocation))
        }
    }, [])

    useEffect(() => {
        setHasResolvedPromotions(false)

        if (!modelName) {
            return
        }

        if (modelsWithActivePromotions.length) {
            const matchingCar = modelsWithActivePromotions.find(
                (modelFromList: SingleModelInterface) =>
                    modelFromList.slug === modelName
            )

            if (matchingCar) {
                dispatch(setSingleCar(matchingCar))
                setHasResolvedPromotions(true)

                return
            }
        }

        if (backendUrl.length) {
            const singleModelCall: FetchData = {
                ENDPOINT: `${addTrailingSlash(backendUrl)}cars/${modelName}`,
                TOKEN: '',
            }

            if (dealerCode !== '') {
                singleModelCall.ENDPOINT = `${singleModelCall.ENDPOINT}/${dealerCode}`
            }

            if (apiQueryParams && previewAPIToken) {
                singleModelCall.ENDPOINT = `${singleModelCall.ENDPOINT}?${QueryParams.PREVIEW_DATE}=${apiQueryParams}`
                singleModelCall.TOKEN = previewAPIToken
            }

            dispatch(fetchCarBySlug(singleModelCall))
                .unwrap()
                .then((response) => {
                    // @ts-ignore-next-line
                    const singleModelResponse: SingleModelInterface = response

                    if (
                        isEmpty(singleModelResponse) ||
                        isEmpty(singleModelResponse.related_promotions)
                    ) {
                        if (!isShowroom) {
                            navigate.push('/')
                        }
                    }

                    setHasResolvedPromotions(true)
                })
                .catch((err) => {
                    console.log(err)
                    setHasResolvedPromotions(true)
                })
        }
    }, [
        modelName,
        backendUrl,
        modelsWithActivePromotions,
        dealerCode,
        apiQueryParams,
        previewAPIToken,
        isShowroom,
        dispatch,
        navigate,
    ])

    const loading = useSelector((state: RootState) => state.general.loading)
    const model = useSelector((state: RootState) => state.general.singleCar)
    const modelExists = Object.keys(model).length
    const activePromotionsExists = !isEmpty(model.related_promotions)
    const bigBannerData = modelExists ? {...model.active_banners[0]} : {}
    const bigBannerDataExists = Object.keys(bigBannerData).length
    const smallBanners = modelExists ? model.active_banners.slice(1) : []
    const isDealerSelected = dealerCode !== ''
    const [openAccordionItemIndex, setOpenAccordionItemIndex] = useState(
        AccordionSettings.ACCORDION_INDEX_CLOSE_ALL_ITEMS
    )
    // Determine if accordion item was clicked by user or triggered programmatically
    const [userClickedAccordion, setUserClickedAccordion] = useState(false)
    const [
        isSinglePromotionWrapperBottomVisible,
        setIsSinglePromotionWrapperBottomVisible,
    ] = useState(false)

    const trackingBrand = useSelector(
        (state: RootState) => state.general.trackingBrand
    )
    const customerType = useSelector(
        (state: RootState) => state.general.customerType
    )
    const dealerName = useSelector(
        (state: RootState) => state.dealerData.dealerName
    )
    const showRFQForm = useSelector(
        (state: RootState) => state.general.showRFQForm
    )

    const {tracking: trackingManager} = useFeatureServices()
    const promotionsByCustomerType: Promotion[] = !activePromotionsExists
        ? []
        : getPromotionsByCustomerType(model.related_promotions)
    const showroomOpenPromotionId = getShowroomOpenPromotionId(
        promotionsByCustomerType,
        userClickedAccordion ? openAccordionItemIndex : null
    )
    const singlePromotionWrapperRef = useRef<HTMLUListElement | null>(null)

    useEffect(() => {
        if (!isShowroom || !props.onStatusChange) {
            return
        }

        props.onStatusChange({
            hasResolvedPromotions,
            hasAnyPromotions: activePromotionsExists,
            modelNames: model.name ? [model.name] : [],
        })
    }, [
        isShowroom,
        hasResolvedPromotions,
        activePromotionsExists,
        model.name,
        props.onStatusChange,
    ])

    useEffect(() => {
        if (modelExists) {
            dispatch(setLoading(false))
        } else if (!model) {
            dispatch(setLoading(true))
        }
    }, [modelExists, model])

    useEffect(() => {
        if (checkTrackingManager(trackingManager) && activePromotionsExists) {
            const openPromo = model.related_promotions[0]
            const modelNameTracking = modelExists ? model.name : modelName

            setTracking(
                trackingManager,
                TrackingAction.VWBasic_GenericFeatureApp_Load,
                getVehiclePromotionsPageloadData(
                    modelNameTracking,
                    {
                        title: openPromo.title,
                        price: openPromo.price,
                    },
                    trackingBrand,
                    isShowroom
                )
            )
        }
        const threshold = isMobile ? 0.05 : 0.1

        const observer = new IntersectionObserver(
            ([entry]) =>
                setIsSinglePromotionWrapperBottomVisible(entry.isIntersecting),
            {
                root: null,
                threshold,
            }
        )

        if (singlePromotionWrapperRef.current) {
            observer.observe(singlePromotionWrapperRef.current)
        }

        return () => observer.disconnect()
    }, [activePromotionsExists])

    function getPromotionsByCustomerType(
        allActivePromos: Promotion[]
    ): Promotion[] {
        if (
            brandFromState === Brands.VW ||
            (brandFromState === Brands.VIC && isShowroom)
        ) {
            return allActivePromos
        }

        return allActivePromos.filter(
            (promo: Promotion) => promo.customer_type === customerType
        )
    }

    function setAccordionItemActive(key: number): void {
        setOpenAccordionItemIndex(key)
    }

    function onAccordionOpen(promoSlug: string): void {
        if (isShowroom) {
            return
        }

        if (promoSlug.length) {
            history.pushState(null, '', `#${promoSlug}`)
            scrollIntoView(promoSlug)

            return
        }

        const currentUrl = window.location.href
        const cleanUrl = currentUrl.split('#')[0]
        history.replaceState({}, document.title, cleanUrl)
    }

    function accordionItemClickHandler(
        itemKey: number,
        // tslint:disable-next-line:typedef
        triggerClick = true,
        promo: Promotion
    ): void {
        // In showroom the promotion open on landing is computed from the
        // backend data, so its automatic opening has nothing to store or track
        if (isShowroom && !triggerClick) {
            return
        }

        setAccordionItemActive(itemKey)
        setUserClickedAccordion(triggerClick)

        // track accordion opening with no interaction from user
        if (!triggerClick) {
            if (checkTrackingManager(trackingManager)) {
                setTracking(
                    trackingManager,
                    TrackingAction.VWBasic_VehiclePromotionsAccordionOpen_Click,
                    getAccordionOpenClickData(
                        model.name,
                        model.slug,
                        promo.title,
                        promo.motorization,
                        trackingBrand,
                        isShowroom
                    )
                )
            }
        }
    }

    function getSlugFromUrl(): string {
        const urlSlug = window.location.hash

        return urlSlug.startsWith('#')
            ? urlSlug.substring(1, urlSlug.length)
            : urlSlug
    }

    function isSlugActive(accordionSlug: string): boolean {
        return getSlugFromUrl() === accordionSlug
    }

    function isItemOpen(
        promoSlug: string,
        promoId: number,
        firstPromoId: number
    ): boolean {
        const isClickedAccordion = openAccordionItemIndex === promoId

        // if the app is loaded with showroom template, only the promotion set
        // in the backend is open on landing, then the items clicked. No scroll:
        // the app may be loaded lazily while the user scrolls the page
        if (isShowroom) {
            return promoId === showroomOpenPromotionId
        }

        // user has interacted with the items and it's the clicked item
        if (userClickedAccordion) {
            return isClickedAccordion
        }

        const isItemFromUrlHash = isSlugActive(promoSlug)
        const isFirstAccordionItem = promoId === firstPromoId
        const urlSlug = getSlugFromUrl()

        // user did not interact with the items
        // no hash from URL
        if (!urlSlug.length) {
            return isFirstAccordionItem
        }

        // hash exists, item is first and from URL hash
        if (isFirstAccordionItem && isItemFromUrlHash) {
            scrollIntoView(promoSlug)

            return true
        }

        // if the item is from URL hash, scroll into it
        if (isItemFromUrlHash) {
            scrollIntoView(promoSlug)
        }

        // by default check if the item is from URL hash
        return isItemFromUrlHash
    }

    function scrollIntoView(elementId: string): void {
        const element = document.getElementById(elementId)
        if (element) {
            setTimeout(() => {
                element.scrollIntoView({behavior: 'smooth'})
            }, 1)
        }
    }

    return activePromotionsExists ? (
        <div>
            {isShowroom ? undefined : (
                <BasicStage
                    title={model.name}
                    ctaText={t('cta.singlePromotion.requestQuote')}
                    ctaUrl={model.estimate_cta_url}
                    imageName={model.teaser_image}
                    imageNameMobile={model.teaser_image_mobile}
                    imageAlt={model.name}
                    modelSlug={model.slug}
                    modelName={model.name}
                    disclaimer={model.disclaimer}
                    showRFQForm={showRFQForm}
                />
            )}

            <SinglePromotionWrapper ref={singlePromotionWrapperRef}>
                <SinglePromotionContainer
                    className={
                        isShowroom ? 'Single-Promotion-is-standalone' : ''
                    }
                >
                    {dealerCode &&
                    dealerName &&
                    !model.in_specialization &&
                    !loading ? (
                        <ModelNotAvailableText
                            tag={TextTag.p}
                            textAlign={TextAlignment.center}
                            appearance={TokenTextAppearance.headline400}
                        >
                            {t('singlePage.noAvailablePromotions')}{' '}
                            <strong>{capitalizeWords(dealerName)}</strong>
                        </ModelNotAvailableText>
                    ) : (
                        <>
                            {promotionsByCustomerType.map(
                                (promo: Promotion) => (
                                    <AccordionItem
                                        {...promo}
                                        key={promo.id}
                                        isOpen={isItemOpen(
                                            promo.slug,
                                            promo.id,
                                            model.related_promotions[0].id
                                        )}
                                        openIndex={
                                            isShowroom
                                                ? showroomOpenPromotionId
                                                : openAccordionItemIndex
                                        }
                                        equipments={promo.related_equipments}
                                        estimate_cta_url={
                                            model.estimate_cta_url
                                        }
                                        modelName={model.name}
                                        modelSlug={model.slug}
                                        onClick={(index, triggerClick) =>
                                            accordionItemClickHandler(
                                                index,
                                                triggerClick,
                                                promo
                                            )
                                        }
                                        onOpen={(slug) => onAccordionOpen(slug)}
                                        onClose={() => onAccordionOpen('')}
                                        isDealerSelected={isDealerSelected}
                                        dealerName={dealerName}
                                        promoScope={promo.type}
                                        isPromoAvailable={
                                            promo.accepted_by_dealer
                                        }
                                    />
                                )
                            )}
                        </>
                    )}
                    {isShowroom ? undefined : (
                        <BannersWrapper>
                            {bigBannerDataExists && bigBannerData.cta_url ? (
                                <BigBanner
                                    {...bigBannerData}
                                    cta_url={bigBannerData.cta_url}
                                    modelName={model.name}
                                    modelSlug={model.slug}
                                />
                            ) : undefined}

                            <SmallBannersWrapper>
                                {smallBanners.length
                                    ? smallBanners.map((banner: Banners) =>
                                          banner.cta_url ? (
                                              <SmallBanner
                                                  {...banner}
                                                  modelName={model.name}
                                                  modelSlug={model.slug}
                                                  key={banner.id}
                                              />
                                          ) : undefined
                                      )
                                    : undefined}
                            </SmallBannersWrapper>
                        </BannersWrapper>
                    )}
                </SinglePromotionContainer>
                {isShowroom ? undefined : (
                    <ShowRoomBannerContainer>
                        <ShowroomBanner
                            modelName={model.name}
                            modelSlug={model.slug}
                            showroomImage={model.showroom_image}
                            showroomUrl={model.showroom_cta_url}
                            showroomCtaLabel={model.showroom_cta_label}
                            showroomDescription={model.showroom_description}
                        />
                    </ShowRoomBannerContainer>
                )}
                {!isShowroom &&
                    isSinglePromotionWrapperBottomVisible &&
                    showRFQForm && (
                        <HorizontalRFQFixedBanner
                            onClick={() => {
                                dispatch(
                                    setFormCycleID(
                                        generateRandomString(
                                            Global.CYCLE_ID_LENGTH
                                        )
                                    )
                                )
                                openCouponLayer()
                            }}
                            ctaLabel={t('cta.singlePromotion.requestQuote')}
                        />
                    )}
            </SinglePromotionWrapper>
        </div>
    ) : loading ? (
        isShowroom ? (
            <div />
        ) : (
            <ShowroomTitle
                modelHasPromos={activePromotionsExists}
                modelName={''}
            />
        )
    ) : (
        <div />
    )
}
